/**
 * Server state (CLAUDE.md 14.1).
 *
 * TanStack Query owns everything that comes from the API. No server data lives in
 * React state or a global store -- that rule exists because the alternative is two
 * sources of truth for a position's inputs, and the one on screen being the stale one.
 *
 * Query keys begin with the active workspace id where the resource is workspace-scoped,
 * so switching workspaces cannot show the previous one's data under the new one's name.
 */
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query'

import { api } from '@/api/client'
import type {
  Drawing,
  ModuleSpec,
  Position,
  Project,
  ProjectBundle,
  Report,
  Revision,
  RevisionDetail,
  SearchResult,
  ShareLink,
  SharedView,
  TraceNode,
} from '@/api/types'

/* --- keys ------------------------------------------------------------------------ */

export const queryKeys = {
  modules: ['modules'] as const,
  projects: (workspaceId: number | null) => ['projects', workspaceId] as const,
  project: (id: number) => ['project', id] as const,
  positions: (projectId: number) => ['positions', projectId] as const,
  position: (id: number) => ['position', id] as const,
  trace: (id: number, verbosity: number) => ['trace', id, verbosity] as const,
  revisions: (id: number) => ['revisions', id] as const,
  revision: (id: number, rev: number) => ['revision', id, rev] as const,
  search: (workspaceId: number | null, query: string) => ['search', workspaceId, query] as const,
  reports: (positionId: number) => ['reports', positionId] as const,
  projectReports: (projectId: number) => ['reports', 'project', projectId] as const,
  shareLinks: (projectId: number) => ['share-links', projectId] as const,
  sharedView: (token: string) => ['shared', token] as const,
}

/* --- meta ------------------------------------------------------------------------ */

export function useModules() {
  return useQuery({
    queryKey: queryKeys.modules,
    queryFn: () =>
      api.get<{ modules: ModuleSpec[]; reserved_short_codes: string[] }>(
        '/meta/modules?admin=true&include_schema=true',
      ),
    // The registry changes on deploy, not during a session.
    staleTime: 5 * 60_000,
  })
}

/* --- projects -------------------------------------------------------------------- */

export function useProjects(workspaceId: number | null, includeDeleted = false) {
  return useQuery({
    queryKey: [...queryKeys.projects(workspaceId), includeDeleted],
    queryFn: () => api.get<Project[]>(`/projects?include_deleted=${includeDeleted}`),
    enabled: workspaceId !== null,
  })
}

export function useProject(projectId: number | null) {
  return useQuery({
    queryKey: queryKeys.project(projectId ?? 0),
    queryFn: () => api.get<Project>(`/projects/${projectId}`),
    enabled: projectId !== null,
  })
}

export function useCreateProject(workspaceId: number | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { name: string; number?: string; client?: string }) =>
      api.post<Project>('/projects', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects(workspaceId) })
    },
  })
}

export function useDeleteProject(workspaceId: number | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (projectId: number) => api.delete(`/projects/${projectId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects(workspaceId) })
    },
  })
}

export function useRestoreProject(workspaceId: number | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (projectId: number) => api.post<Project>(`/projects/${projectId}/restore`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects(workspaceId) })
    },
  })
}

/**
 * Search projects *and* positions, server-side.
 *
 * The list page used to filter the array it had already loaded, which meant a
 * position could never be found -- the client never had them. A position number is
 * exactly what an engineer types when returning to a job, so the search that cannot
 * find one is the search that does not work.
 *
 * `keepPreviousData` holds the previous hits while the next query is in flight, so
 * typing does not empty the list between keystrokes.
 */
export function useProjectSearch(workspaceId: number | null, query: string) {
  const trimmed = query.trim()
  return useQuery({
    queryKey: queryKeys.search(workspaceId, trimmed),
    queryFn: () =>
      api.get<SearchResult>(`/projects/search?q=${encodeURIComponent(trimmed)}&limit=20`),
    enabled: workspaceId !== null && trimmed.length > 0,
    placeholderData: keepPreviousData,
  })
}

/**
 * Import a project bundle.
 *
 * The bundle is parsed in the browser and posted as the request body, so a file that
 * is not JSON at all is refused before it costs a round trip -- and the server's own
 * `error.project.import_*` keys carry every refusal it makes after that.
 */
export function useImportProject(workspaceId: number | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (bundle: ProjectBundle) => api.post<Project>('/projects/import', bundle),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects(workspaceId) })
    },
  })
}

/* --- positions ------------------------------------------------------------------- */

export function usePositions(projectId: number | null) {
  return useQuery({
    queryKey: queryKeys.positions(projectId ?? 0),
    queryFn: () => api.get<Position[]>(`/projects/${projectId}/positions`),
    enabled: projectId !== null,
  })
}

export function usePosition(
  positionId: number | null,
  options?: Partial<UseQueryOptions<Position>>,
) {
  return useQuery({
    queryKey: queryKeys.position(positionId ?? 0),
    queryFn: () => api.get<Position>(`/positions/${positionId}`),
    enabled: positionId !== null,
    ...options,
  })
}

export function useCreatePosition(projectId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { name: string; module_id: string; inputs?: Record<string, unknown> }) =>
      api.post<Position>(`/projects/${projectId}/positions`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.positions(projectId) })
    },
  })
}

/**
 * Save a position's inputs under optimistic locking (10.2).
 *
 * `If-Match` carries the row version. A 409 means someone else changed it, and the
 * response names who and when so the UI can say so rather than showing a bare
 * "conflict" -- which an engineer can do nothing with.
 */
export function useUpdatePosition(positionId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      rowVersion,
      ...body
    }: {
      rowVersion: number
      name?: string
      inputs?: Record<string, unknown>
    }) => api.patchWithMatch<Position>(`/positions/${positionId}`, body, rowVersion),
    onSuccess: (position) => {
      queryClient.setQueryData(queryKeys.position(positionId), position)
      void queryClient.invalidateQueries({ queryKey: queryKeys.positions(position.project_id) })
    },
  })
}

export function useCalculatePosition(positionId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<Position>(`/positions/${positionId}/calculate`),
    onSuccess: (position) => {
      queryClient.setQueryData(queryKeys.position(positionId), position)
      void queryClient.invalidateQueries({ queryKey: ['trace', positionId] })
      void queryClient.invalidateQueries({ queryKey: queryKeys.positions(position.project_id) })
    },
  })
}

export function useReviewPosition(positionId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { action: 'submit' | 'check' | 'approve' | 'reject'; note?: string }) =>
      api.post<Position>(`/positions/${positionId}/review`, input),
    onSuccess: (position) => {
      queryClient.setQueryData(queryKeys.position(positionId), position)
    },
  })
}

export function usePositionTrace(positionId: number | null, verbosity: number) {
  return useQuery({
    queryKey: queryKeys.trace(positionId ?? 0, verbosity),
    queryFn: () =>
      api.get<{ trace: TraceNode }>(`/positions/${positionId}/trace?verbosity=${verbosity}`),
    enabled: positionId !== null,
  })
}

export function useDuplicatePosition(projectId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (positionId: number) => api.post<Position>(`/positions/${positionId}/duplicate`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.positions(projectId) })
    },
  })
}

/**
 * Move a position to another project.
 *
 * Both projects' lists are invalidated: the source loses a row and the destination
 * gains one, and a stale list on either side would show the same position twice or
 * not at all.
 */
export function useMovePosition(projectId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      positionId,
      targetProjectId,
    }: {
      positionId: number
      targetProjectId: number
    }) =>
      api.post<Position>(`/positions/${positionId}/move`, { target_project_id: targetProjectId }),
    onSuccess: (position) => {
      queryClient.setQueryData(queryKeys.position(position.id), position)
      void queryClient.invalidateQueries({ queryKey: queryKeys.positions(projectId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.positions(position.project_id) })
    },
  })
}

export function useDeletePosition(projectId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (positionId: number) => api.delete<void>(`/positions/${positionId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.positions(projectId) })
    },
  })
}

/**
 * Reorder the positions of a project.
 *
 * Optimistic, and rolled back on failure. A drag that visually snaps back a second
 * later is confusing; a drag that appears to work and silently did not is worse --
 * the engineer's next report has the positions in an order the server never agreed
 * to. So the new order is shown immediately, and a failure restores exactly the list
 * that was on screen before the drag.
 */
export function useReorderPositions(projectId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (orderedIds: number[]) =>
      api.post<Position[]>(`/projects/${projectId}/positions/reorder`, {
        ordered_ids: orderedIds,
      }),
    onMutate: async (orderedIds) => {
      const key = queryKeys.positions(projectId)
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<Position[]>(key)
      if (previous) {
        const byId = new Map(previous.map((position) => [position.id, position]))
        const next = orderedIds
          .map((id, index) => {
            const position = byId.get(id)
            // `seq` is renumbered by the server; mirroring it here keeps the row
            // labels (P01, P02...) consistent with the order being shown.
            return position ? { ...position, seq: index + 1 } : null
          })
          .filter((position): position is Position => position !== null)
        queryClient.setQueryData(key, next)
      }
      return { previous }
    },
    onError: (_error, _orderedIds, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKeys.positions(projectId), context.previous)
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.positions(projectId) })
    },
  })
}

export function useRevisions(positionId: number | null) {
  return useQuery({
    queryKey: queryKeys.revisions(positionId ?? 0),
    queryFn: () => api.get<Revision[]>(`/positions/${positionId}/revisions`),
    enabled: positionId !== null,
  })
}

/** One revision in full. Fetched only when the engineer opens it -- inputs are not free. */
export function useRevisionDetail(positionId: number | null, rev: number | null) {
  return useQuery({
    queryKey: queryKeys.revision(positionId ?? 0, rev ?? 0),
    queryFn: () => api.get<RevisionDetail>(`/positions/${positionId}/revisions/${rev}`),
    enabled: positionId !== null && rev !== null,
  })
}

/**
 * Restore a revision's inputs (CLAUDE.md 5.7, 2.2).
 *
 * This puts the *inputs* back and nothing else: the stored result is not restored,
 * the review state resets, and the position is stale until it is recalculated. The
 * caller must say so before it happens -- see `RevisionsPanel`.
 */
export function useRestoreRevision(positionId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (rev: number) =>
      api.post<Position>(`/positions/${positionId}/revisions/${rev}/restore`),
    onSuccess: (position) => {
      queryClient.setQueryData(queryKeys.position(positionId), position)
      void queryClient.invalidateQueries({ queryKey: queryKeys.revisions(positionId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.positions(position.project_id) })
    },
  })
}

/* --- outputs --------------------------------------------------------------------- */

export function useReports(positionId: number | null) {
  return useQuery({
    queryKey: queryKeys.reports(positionId ?? 0),
    queryFn: () => api.get<Report[]>(`/reports?position_id=${positionId}`),
    enabled: positionId !== null,
  })
}

/**
 * Request a report, then poll until it settles.
 *
 * The 202-and-poll shape of section 13 is a server-side contract, so the client must
 * poll even when the server happens to render inline -- the response could be `done`
 * on the first read, and that is simply a poll that finished immediately.
 */
export function useCreateReport(positionId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { lang?: string; doc_status?: string; verbosity?: number }) =>
      api.post<Report>(`/positions/${positionId}/report`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.reports(positionId) })
    },
  })
}

/**
 * Request a report for a whole project (CLAUDE.md 8.1).
 *
 * The same 202-and-poll contract as the position report -- deliberately, because a
 * project report is the slower of the two and the client must not assume otherwise.
 * FOR CONSTRUCTION is not offered here: the server authorises that status against a
 * single approved position, so a project report can never carry it.
 */
export function useCreateProjectReport(projectId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { lang?: string; doc_status?: string; verbosity?: number }) =>
      api.post<Report>(`/projects/${projectId}/report`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.projectReports(projectId) })
    },
  })
}

export function useReportPolling(reportId: number | null, enabled: boolean) {
  return useQuery({
    queryKey: ['report', reportId],
    queryFn: () => api.get<Report>(`/reports/${reportId}`),
    enabled: reportId !== null && enabled,
    // Stops as soon as the job reaches a terminal state, so a finished report does
    // not keep a timer alive for the rest of the session.
    refetchInterval: (query) => {
      const status = query.state.data?.status
      return status === 'queued' || status === 'rendering' ? 1500 : false
    },
  })
}

export function useCreateDrawing(positionId: number) {
  return useMutation({
    mutationFn: (input: { format: 'dxf' | 'pdf' }) =>
      api.post<Drawing>(`/positions/${positionId}/drawing`, input),
  })
}

/* --- external sharing (CLAUDE.md 11.4) ------------------------------------------- */

export function useShareLinks(projectId: number | null, enabled = true) {
  return useQuery({
    queryKey: queryKeys.shareLinks(projectId ?? 0),
    queryFn: () => api.get<ShareLink[]>(`/share-links?project_id=${projectId}`),
    enabled: projectId !== null && enabled,
  })
}

/**
 * Mint a link.
 *
 * The response is the only time the token exists outside the recipient's browser, so
 * the caller must show it immediately -- there is no "show token" action to build
 * later, by design.
 */
export function useCreateShareLink(projectId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      scope: ShareLink['scope']
      project_id?: number
      position_id?: number
      report_id?: number
      password?: string
      expires_days?: number
      label?: string
    }) => api.post<ShareLink>('/share-links', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.shareLinks(projectId) })
    },
  })
}

export function useRevokeShareLink(projectId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (linkId: number) => api.delete<void>(`/share-links/${linkId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.shareLinks(projectId) })
    },
  })
}

/**
 * The recipient's view. Unauthenticated: whoever opens this has a token and nothing
 * else, and must not be asked to sign in.
 *
 * The password travels in a header rather than the URL, because a URL ends up in
 * proxy logs, browser history and the referrer of every outbound link on the page --
 * and the whole point of the password is that the URL alone is not enough.
 */
export function useSharedView(token: string | undefined, password: string | null) {
  return useQuery({
    queryKey: [...queryKeys.sharedView(token ?? ''), password !== null],
    queryFn: () =>
      api.request<SharedView>(`/s/${token}`, {
        method: 'GET',
        auth: false,
        headers: password ? { 'X-Share-Password': password } : undefined,
      }),
    enabled: Boolean(token),
    // A 401 asking for the password is an answer, not a failure to retry.
    retry: false,
  })
}
